import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * BottomSheet — mobile-native slide-up panel with drag-to-close.
 *
 * CRITICAL: All transforms/transitions on the sheet and backdrop are controlled
 * exclusively via refs (direct DOM manipulation). React inline styles are NOT
 * used for transform/opacity because parent re-renders (e.g. real-time price
 * updates) would overwrite the drag position mid-gesture, causing the bug where
 * content appears to be "pushed down" inside the sheet.
 *
 * Props:
 *  isOpen    — boolean
 *  onClose   — () => void
 *  title     — optional string shown in the sheet header
 *  children  — content
 *  maxHeight — CSS max-height string, default '85vh'
 *  header    — optional JSX rendered as a STICKY header (stays fixed while
 *              content scrolls). Also acts as a drag-to-close zone.
 */
export default function BottomSheet({ isOpen, onClose, title, children, maxHeight = '85vh', header }) {
  const sheetRef = useRef(null);
  const backdropRef = useRef(null);
  const contentRef = useRef(null);
  const handleRef = useRef(null);
  const headerRef = useRef(null);

  // mounted = DOM is present; visible = entrance animation has played
  const [mounted, setMounted] = useState(false);
  const visibleRef = useRef(false);

  // Desktop detection for centered modal vs bottom sheet
  const isDesktop = () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches;

  // Drag state — all in refs to avoid re-renders during gesture
  const dragState = useRef({
    active: false,
    startY: 0,
    currentY: 0,
    startTime: 0,
    lastY: 0,
    lastTime: 0,
    velocity: 0,
  });

  // ─── Mount / Unmount with exit animation ───────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
    } else if (mounted) {
      // Animate out via ref then unmount
      const sheet = sheetRef.current;
      const backdrop = backdropRef.current;
      if (sheet && visibleRef.current) {
        sheet.style.transition = 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1), opacity 280ms ease-out';
        sheet.style.transform = isDesktop() ? 'translate(-50%, -40%) scale(0.97)' : 'translateY(100%)';
        sheet.style.opacity = isDesktop() ? '0' : '1';
      }
      if (backdrop && visibleRef.current) {
        backdrop.style.transition = 'opacity 280ms ease-out';
        backdrop.style.opacity = '0';
      }
      visibleRef.current = false;
      const timer = setTimeout(() => setMounted(false), 320);
      return () => clearTimeout(timer);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Animate IN after mount (two rAF for paint → animate sequence)
  useEffect(() => {
    if (!mounted) return;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const sheet = sheetRef.current;
      const backdrop = backdropRef.current;
      if (sheet) {
        sheet.style.transition = 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1), opacity 280ms ease-out';
        sheet.style.transform = isDesktop() ? 'translate(-50%, -50%)' : 'translateY(0)';
        sheet.style.opacity = '1';
      }
      if (backdrop) {
        backdrop.style.transition = 'opacity 280ms ease-out';
        backdrop.style.opacity = '1';
      }
      visibleRef.current = true;
    }));
  }, [mounted]);

  // Lock body scroll + hide bottom nav when mounted
  useEffect(() => {
    if (!mounted) return;
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.classList.add('bottomsheet-open');
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.classList.remove('bottomsheet-open');
      window.scrollTo(0, scrollY);
    };
  }, [mounted]);

  // Close on Escape
  useEffect(() => {
    if (!mounted) return;
    const handleKey = (e) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [mounted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on browser back button
  useEffect(() => {
    if (!mounted) return;
    window.history.pushState({ bottomSheet: true }, '');
    const handlePop = () => dismiss();
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [mounted]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Dismiss helper — animate out then call onClose ────────────────────
  const dismissing = useRef(false);
  const dismiss = useCallback(() => {
    if (dismissing.current) return;
    dismissing.current = true;
    const sheet = sheetRef.current;
    const backdrop = backdropRef.current;
    if (sheet) {
      sheet.style.transition = 'transform 280ms cubic-bezier(0.32, 0.72, 0, 1), opacity 280ms ease-out';
      sheet.style.transform = isDesktop() ? 'translate(-50%, -40%) scale(0.97)' : 'translateY(100%)';
      sheet.style.opacity = isDesktop() ? '0' : '1';
    }
    if (backdrop) {
      backdrop.style.transition = 'opacity 280ms ease-out';
      backdrop.style.opacity = '0';
    }
    setTimeout(() => {
      dismissing.current = false;
      onClose();
    }, 290);
  }, [onClose]);

  // ─── Drag gesture — refs only, no re-renders ──────────────────────────
  const applyDragTransform = useCallback((dy) => {
    const sheet = sheetRef.current;
    const backdrop = backdropRef.current;
    if (sheet) {
      sheet.style.transition = 'none';
      sheet.style.transform = `translateY(${Math.max(0, dy)}px)`;
    }
    if (backdrop) {
      backdrop.style.transition = 'none';
      backdrop.style.opacity = String(Math.max(0, 1 - dy / 350));
    }
  }, []);

  const snapBack = useCallback(() => {
    const sheet = sheetRef.current;
    const backdrop = backdropRef.current;
    if (sheet) {
      sheet.style.transition = 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)';
      sheet.style.transform = 'translateY(0)';
    }
    if (backdrop) {
      backdrop.style.transition = 'opacity 300ms ease-out';
      backdrop.style.opacity = '1';
    }
  }, []);

  // ─── Unified drag primitives ──────────────────────────────────────────
  const beginDrag = useCallback((clientY) => {
    const now = Date.now();
    const ds = dragState.current;
    ds.active = true;
    ds.startY = clientY;
    ds.currentY = clientY;
    ds.startTime = now;
    ds.lastY = clientY;
    ds.lastTime = now;
    ds.velocity = 0;
  }, []);

  const updateDrag = useCallback((clientY) => {
    const ds = dragState.current;
    if (!ds.active) return;
    const now = Date.now();
    const dt = now - ds.lastTime;
    if (dt > 0) ds.velocity = (clientY - ds.lastY) / dt; // px/ms, + = downward
    ds.lastY = clientY;
    ds.lastTime = now;
    ds.currentY = clientY;
    const dy = clientY - ds.startY;
    applyDragTransform(dy > 0 ? dy : dy * 0.15); // resistance dragging up
  }, [applyDragTransform]);

  const finishDrag = useCallback(() => {
    const ds = dragState.current;
    if (!ds.active) return;
    ds.active = false;
    const dy = ds.currentY - ds.startY;
    const velocity = ds.velocity; // px/ms
    const DISTANCE_THRESHOLD = 120; // px
    const VELOCITY_THRESHOLD = 0.4; // px/ms (~400px/s)
    if (dy > DISTANCE_THRESHOLD || (dy > 40 && velocity > VELOCITY_THRESHOLD)) {
      dismiss();
    } else {
      snapBack();
    }
  }, [dismiss, snapBack]);

  // Handle/header drag zone — always drag immediately (preventDefault scroll)
  const onZoneStart = useCallback((e) => {
    if (e.target.closest('button')) return;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    beginDrag(clientY);
    if (e.cancelable) e.preventDefault();
  }, [beginDrag]);

  const onZoneMove = useCallback((e) => {
    if (!dragState.current.active) return;
    if (e.cancelable) e.preventDefault();
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    updateDrag(clientY);
  }, [updateDrag]);

  // ─── Attach pointer listeners ──────────────────────────────────────────
  useEffect(() => {
    const handleEl = handleRef.current;
    const headerEl = headerRef.current;
    const contentEl = contentRef.current;
    if (!handleEl || !mounted) return;

    // Drag zones that always grab: handle bar + sticky header
    const zones = [handleEl, headerEl].filter(Boolean);
    zones.forEach((el) => {
      el.addEventListener('touchstart', onZoneStart, { passive: false });
      el.addEventListener('touchmove', onZoneMove, { passive: false });
      el.addEventListener('touchend', finishDrag, { passive: true });
    });

    // Content area: native-style — only take over when scrolled to top and
    // the user pulls DOWN. Otherwise let the browser scroll normally.
    const TAKEOVER = 6; // px of downward travel before grabbing the sheet
    const onContentStart = (e) => {
      const ds = dragState.current;
      ds.active = false;
      ds.pointerStartY = e.touches[0].clientY;
      // Opt-out zones (e.g. interactive chart) must not trigger sheet drag
      ds.noDrag = !!(e.target.closest && e.target.closest('[data-no-sheet-drag]'));
    };
    const onContentMove = (e) => {
      const ds = dragState.current;
      const clientY = e.touches[0].clientY;
      if (ds.active) {
        if (e.cancelable) e.preventDefault();
        updateDrag(clientY);
        return;
      }
      if (ds.noDrag) return;
      const dy = clientY - ds.pointerStartY;
      if (dy > TAKEOVER && contentEl.scrollTop <= 0) {
        beginDrag(clientY); // reset origin → no jump
        if (e.cancelable) e.preventDefault();
        updateDrag(clientY);
      }
    };
    const onContentEnd = () => { if (dragState.current.active) finishDrag(); };
    if (contentEl) {
      contentEl.addEventListener('touchstart', onContentStart, { passive: true });
      contentEl.addEventListener('touchmove', onContentMove, { passive: false });
      contentEl.addEventListener('touchend', onContentEnd, { passive: true });
    }

    // Mouse (desktop) — drag from handle/header only
    const onMouseDown = (e) => {
      if (e.target.closest('button')) return;
      onZoneStart(e);
      const onMM = (ev) => onZoneMove(ev);
      const onMU = () => { finishDrag(); document.removeEventListener('mousemove', onMM); document.removeEventListener('mouseup', onMU); };
      document.addEventListener('mousemove', onMM);
      document.addEventListener('mouseup', onMU);
    };
    zones.forEach((el) => el.addEventListener('mousedown', onMouseDown));

    return () => {
      zones.forEach((el) => {
        el.removeEventListener('touchstart', onZoneStart);
        el.removeEventListener('touchmove', onZoneMove);
        el.removeEventListener('touchend', finishDrag);
        el.removeEventListener('mousedown', onMouseDown);
      });
      if (contentEl) {
        contentEl.removeEventListener('touchstart', onContentStart);
        contentEl.removeEventListener('touchmove', onContentMove);
        contentEl.removeEventListener('touchend', onContentEnd);
      }
    };
  }, [mounted, onZoneStart, onZoneMove, finishDrag, beginDrag, updateDrag]);

  // Subtle border/shadow on sticky header once content scrolls under it
  useEffect(() => {
    if (!mounted) return;
    const contentEl = contentRef.current;
    const headerEl = headerRef.current;
    if (!contentEl || !headerEl) return;
    const onScroll = () => {
      headerEl.style.boxShadow = contentEl.scrollTop > 2
        ? '0 6px 16px -8px rgba(0,0,0,0.45)'
        : 'none';
      headerEl.style.borderBottomColor = contentEl.scrollTop > 2
        ? 'var(--color-border)'
        : 'transparent';
    };
    contentEl.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => contentEl.removeEventListener('scroll', onScroll);
  }, [mounted]);

  // ─── Render ────────────────────────────────────────────────────────────
  if (!mounted) return null;

  return createPortal((
    <div className="fixed inset-0 z-[10000]">
      {/* Backdrop — opacity controlled via ref. Uses theme overlay token. */}
      <div
        ref={backdropRef}
        className="absolute inset-0 backdrop-blur-[2px]"
        style={{ opacity: 0, backgroundColor: 'var(--color-overlay)' }}
        onMouseDown={dismiss}
        onTouchStart={dismiss}
      />

      {/* Sheet panel — transform controlled via ref, NOT React style.
          Initial position is off-screen (100%); animate-in effect sets it to 0. */}
      <div
        ref={sheetRef}
        className="absolute bottom-0 inset-x-0 z-10 rounded-t-3xl md:rounded-3xl flex flex-col overflow-hidden will-change-transform border-t md:border theme-border md:bottom-auto md:top-1/2 md:left-1/2 md:inset-auto"
        style={{
          maxHeight,
          transform: typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches ? 'translate(-50%, -40%) scale(0.97)' : 'translateY(100%)',
          opacity: typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches ? 0 : 1,
          backgroundColor: 'var(--color-surface)',
          boxShadow: 'var(--shadow-surface)',
          maxWidth: '600px',
          width: '100%',
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        {/* ─ Drag handle bar — compact (reduced top spacing) ─ */}
        <div ref={handleRef} className="shrink-0 touch-none">
          <div className="flex justify-center pt-2.5 pb-1.5 cursor-grab active:cursor-grabbing">
            <div className="w-9 h-1.5 rounded-full" style={{ backgroundColor: 'var(--color-border)' }} />
          </div>
        </div>

        {/* Sticky header — stays fixed while content scrolls; also a drag zone */}
        {header ? (
          <div
            ref={headerRef}
            className="shrink-0 touch-none border-b transition-shadow"
            style={{ backgroundColor: 'var(--color-surface)', borderBottomColor: 'transparent' }}
          >
            {header}
          </div>
        ) : title ? (
          <div
            ref={headerRef}
            className="shrink-0 px-5 pb-3 pt-1 flex items-center justify-between border-b"
            style={{ backgroundColor: 'var(--color-surface)', borderBottomColor: 'transparent' }}
          >
            <h3 className="text-lg font-bold theme-text-primary">{title}</h3>
            <button
              onClick={dismiss}
              className="w-8 h-8 flex items-center justify-center rounded-full theme-surface-muted theme-text-secondary transition-colors"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        ) : null}

        {/* Scrollable content area */}
        <div
          ref={contentRef}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-hide"
          style={{ paddingBottom: 'calc(2.5rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <div className="px-4 pt-3">
            {children}
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}
